import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import { Docker, K8s, Linux, Windows
} from "../../components/v2/icons";
import AgentIntegration from "../../components/v2/agent-integration";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import '../../styles/scss/_faq-section.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import { APM, Logs, SreAgent } from "../../components/v2/icons-v2";

export default function InfraMonitoring() {
    const { t } = useTranslation("common");

    const featuresData = t('product.infrastructure-monitoring.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.infrastructure-monitoring.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.infrastructure-monitoring.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.infrastructure-monitoring.productTour', { returnObjects: true }) as any

    const agentIntegrationData = t('product.infrastructure-monitoring.agentIntegration', { returnObjects: true }) as any

    const agentDeployData = t('product.shared.agentDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: agent.key === 'kubernetes' ? <K8s /> :
            agent.key === 'linux' ? <Linux /> :
                agent.key === 'docker' ? <Docker /> :
                    agent.key === 'windows' ? <Windows /> :
                        <span className={"text-gradient"}>450+</span>
    }))

    const productOverviewData = {
        title: "Product overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/workflow/infrastructure-monitoring/container-monitoring",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Middleware Infrastructure Monitoring provides deep system visibility, high-cardinality metrics, a customized dashboard, correlated incident context, and alerts, helping platform engineers and SREs to reduce downtime, lower MTTR, and effortlessly optimize cloud and hybrid system performance. It replaces threshold-based, reactive tooling with observability-driven intelligence that is easy to use and requires less maintenance.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "Logs",
                link: "/product/log-monitoring/",
                icon: <Logs />
            },
            {
                title: "OpsAI",
                link: "/product/ops-ai/",
                icon: <SreAgent />
            },
            {
                title: "APM",
                link: "/product/apm/",
                icon: <APM />
            },
        ],
    };

    return (
        <>

            <ProductHeroSection
                title={<h1>{t('product.infrastructure-monitoring.hero.title')} <span>{t('product.infrastructure-monitoring.hero.titleHighlight')}</span></h1>}
                description={t('product.infrastructure-monitoring.hero.description')}
                img={"/images/v2/products/Infrastructure-monitoring.webp"}
                imgMobile={"/images/v2/products/Infrastructure-monitoring-mobile.webp"}
                imgWidth={1548}
                imgHeight={1180}
                preloadImg
                imgAlt={t('product.infrastructure-monitoring.hero.imageAlt')}
                hasActions={true}
                tips={t('product.hero.tips')}
            />

            <ProductOverviewSection productOverviewData={productOverviewData} />

            <FeaturesMonitoringV2 productFeatures={features} tourId={"infra-tour"} />

            <CallToActionDocsLink
                title={'Try It for Free and Experience Complete Infrastructure Visibility.'}
                docsLink="https://docs.middleware.io/workflow/infrastructure-monitoring/container-monitoring"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductTour tourSrc={"https://app.supademo.com/embed/clzgu343l15029x77tbk4rq0b?embed_v=2"}
                title={productTourData.title} tourId={"infra-tour"} description={productTourData.description} titleChanged={<h2>Take an <span className="text-gradient">Interactive Tour </span> for Infrastructure Monitoring Tool</h2>} />

            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <Trans
                            i18nKey="product.infrastructure-monitoring.agentIntegration.title"
                            components={{
                                highlight: <span className="text-gradient" />
                            }}
                        />
                    </h2>
                }
                description={agentIntegrationData.description}
                contactUsVariant="infra-monitoring"
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')} />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.infrastructure-monitoring.faq.title')}</h2>
                    <p>{t('product.infrastructure-monitoring.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion
                            faqs={faqs.map((faq: { id: number; header: string; text: string; textHtml?: string }) => ({
                                id: faq.id,
                                header: faq.header,
                                answerPlain: faq.text,
                                text:
                                    faq.textHtml != null && faq.textHtml !== ""
                                        ? parse(faq.textHtml)
                                        : <p>{faq.text}</p>,
                            }))}
                            showGroups={true}
                        />
                    </div>
                </div>
            </section>

        </>
    )
}